import { createTheme } from '@material-ui/core/styles'
import { useMemo } from 'react'

export const useAppTheme = ({
  mode,
  primaryColorLight,
  primaryColorDark,
  appLogoLight,
  appLogoDark,
  appLogoFavicon,
  appLogoAdImg,
  appName,
  calculatedLogoLight,
  calculatedLogoDark,
  locale
}) => {
  return useMemo(
    () =>
      createTheme(
        {
          overrides: {
            MuiFormHelperText: {
              root: {
                fontSize: '0.75rem',
                fontStyle: 'italic',
                marginTop: 4,
                '&.Mui-error': {
                  color: '#F2385A',
                  fontWeight: 500,
                  textTransform: 'none',
                  marginLeft: 0
                }
              }
            },

            MuiOutlinedInput: {
              root: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#DBDBDB' : '#323f66'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#1A84FF' : '#B0B0B0'
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: mode === 'light' ? '#1A84FF' : '#046AE0',
                    borderWidth: '2px'
                  }
                }
              }
            },

            // Para inputs com borda embaixo
            MuiInput: {
              root: {
                '&:before': {
                  borderBottom: `1px solid ${
                    mode === 'light' ? '#DBDBDB' : '#323f66'
                  }`
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: `2px solid ${
                    mode === 'light' ? '#DBDBDB' : '#323f66'
                  }`
                },
                '&.Mui-focused:after': {
                  borderBottom: `2px solid ${
                    mode === 'light' ? '#1A84FF' : '#046AE0'
                  }`
                }
              }
            },

            // Para TextField especificamente
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#DBDBDB' : '#323f66'
                },
                '& .MuiInputBase-root.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: mode === 'light' ? '#1A84FF' : '#046AE0',
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: mode === 'light' ? '#1A84FF' : '#046AE0'
                }
              }
            },

            // Para labels
            MuiFormLabel: {
              root: {
                '&.Mui-focused': {
                  color: mode === 'light' ? '#1A84FF' : '#046AE0'
                }
              }
            },

            // Para diálogos
            MuiDialog: {
              paper: {
                borderRadius: 16,
                backgroundColor: mode === 'light' ? '#fff' : '#1e1e25',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)'
              }
            },
            MuiDialogTitle: {
              root: {
                fontWeight: 600,
                fontSize: '1.25rem',
                color: mode === 'light' ? primaryColorLight : primaryColorDark,
                padding: '8px 24px'
              }
            },
            MuiDialogContent: {
              root: {
                backgroundColor: mode === 'light' ? '#fff' : '#1e1e25'
              }
            },
            MuiDialogActions: {
              root: {
                padding: '10px 24px',
                backgroundColor: mode === 'light' ? '#fff' : '#1e1e25'
              }
            },
            // Para os componentes dentro do Dialog
            MuiPaper: {
              root: {
                '&.MuiDialog-paper': {
                  backgroundColor: mode === 'light' ? '#fff' : '#1e1e25'
                }
              }
            },

            // Customização do Switch
            MuiSwitch: {
              root: {
                width: 42,
                height: 26,
                padding: 0
              },
              switchBase: {
                padding: 1,
                '&$checked': {
                  transform: 'translateX(16px)',
                  color: '#fff',
                  '& + $track': {
                    backgroundColor: '#1A84FF',
                    opacity: 1,
                    border: 'none'
                  }
                },
                '&$focusVisible': {
                  // Correto: usa o seletor de estado
                  '& $thumb': {
                    color: '#1A84FF',
                    border: '6px solid #fff'
                  }
                }
              },
              thumb: {
                width: 24,
                height: 24
              },
              track: {
                borderRadius: 26 / 2,
                border: `1px solid ${mode === 'light' ? '#DBDBDB' : '#323f66'}`,
                backgroundColor: mode === 'light' ? '#E7E7E7' : '#666',
                opacity: 1,
                transition:
                  'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)'
              },
              checked: {},
              focusVisible: {},
              colorPrimary: {
                '&$checked': {
                  color: '#fff',
                  '& + $track': {
                    backgroundColor: '#1A84FF'
                  }
                }
              },
              colorSecondary: {
                '&$checked': {
                  color: '#fff',
                  '& + $track': {
                    backgroundColor: '#1A84FF'
                  }
                }
              }
            },

            // Adicionando estilo para Select, Autocomplete e Chip
            MuiSelect: {
              root: {
                textTransform: 'uppercase',
                fontSize: '14px'
              },
              select: {
                textTransform: 'uppercase',
                fontSize: '14px'
              }
            },
            MuiAutocomplete: {
              inputRoot: {
                textTransform: 'uppercase',
                fontSize: '14px'
              },
              option: {
                textTransform: 'uppercase',
                fontSize: '14px',
                padding: '0px 8px',
                margin: '0'
              },
              tag: {
                textTransform: 'uppercase'
              }
            },
            MuiChip: {
              root: {
                textTransform: 'uppercase'
              },
              label: {
                textTransform: 'uppercase'
              }
            },
            MuiMenuItem: {
              root: {
                textTransform: 'uppercase',
                fontSize: '14px',
                margin: '0',
                minHeight: '32px'
              }
            }
          },
          typography: {
            fontFamily: 'Inter, sans-serif'
          },
          scrollbarStyles: {
            '&::-webkit-scrollbar': {
              width: '2px'
            },
            '&::-webkit-scrollbar-thumb': {
              boxShadow: 'none',
              borderRadius: '1px',
              backgroundColor: mode === 'light' ? '#F3F3F3' : '#d2c5cb'
            }
          },
          scrollbarStylesSoft: {
            '&::-webkit-scrollbar': {
              width: '2px'
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '1px',
              backgroundColor: mode === 'light' ? '#F3F3F3' : '#d2c5cb'
            }
          },
          palette: {
            type: mode,
            primary: {
              main: '#1A84FF'
            },
            secondary: {
              main: '#7B7B7B'
            },
            background: {
              default: mode === 'dark' ? '#1e1e25' : '#e7e7e7',
              paper: mode === 'dark' ? '#27293d' : '#fff'
            },

            // SEMANTIC COLORS
            textPrimary:
              mode === 'light' ? primaryColorLight : primaryColorDark,
            focusedText: mode === 'light' ? '#1A84FF' : '#046AE0',
            dark: { main: mode === 'light' ? '#333333' : '#1e1e25' },
            light: { main: mode === 'light' ? '#F3F3F3' : '#1e1e25' },
            tabHeaderBackground: mode === 'light' ? '#EEE' : '#666',
            fancyBackground: mode === 'light' ? '#E7E7E7' : '#1e1e25',
            ticketzproad: { main: '#39ACE7', contrastText: 'white' },
            dialog: { main: mode === 'light' ? '#fff' : '#1e1e25' },
            switch: { main: '#1A84FF' },

            gradient: {
              main: 'linear-gradient(90deg, rgba(25,127,255,1) 0%, #25d3ff 100%)'
            },

            // COLORS BY HUE

            white_100: { main: '#fff' },

            blue_50: { main: '#E9F3FF' },
            blue_100: { main: '#25d3ff' },
            blue_300: { main: '#1A84FF' },
            blue_400: { main: '#046AE0' },
            blue_500: { main: '#333653' },
            blue_600: { main: '#2D3436' },

            gray_20: { main: '#fafafa' },
            gray_50: { main: '#f5f5f5' },
            gray_100: { main: '#E7E7E7' },
            gray_300: { main: '#B0B0B0' },
            gray_400: { main: '#7B7B7B' },
            gray_stroke: { main: '#DBDBDB' },

            red_50: { main: '#FFE2E7' },
            red_100: { main: '#F2385A' },
            red_200: { main: '#FFDAE1' },
            red_400: { main: '#FF7675' },
            red_500: { main: '#E17055' },
            red_600: { main: '#D63031' },

            green_50: { main: '#DBFFCA' },
            green_100: { main: '#36B1BF' },
            green_200: { main: '#6BC143' },
            green_300: { main: '#dcf8c6' },
            green_400: { main: '#005c4b' },
            green_500: { main: '#58a733' },

            green_whatsApp: { main: '#25D366' },
            green_whatsApp_100: { main: '#dcf8c6' },
            green_whatsApp_300: { main: '#005c4b' },
            green_whatsApp_700: { main: '#013a2f' },

            yellow_100: { main: '#FFF2B4' },
            yellow_200: { main: '#fff3cd' },
            yellow_300: { main: '#ffeaa7' },
            yellow_400: { main: '#FDCB6E' },
            yellow_500: { main: '#F39C12' },

            orange_400: { main: '#FFB347' },
            orange_600: { main: '#E6851A' },

            pink_400: { main: '#D25A9B' },

            dark_50: { main: '#d2c5cb' },
            dark_200: { main: '#303030' },
            dark_300: { main: '#333653' },
            dark_400: { main: '#27293d' },
            dark_500: { main: '#4D4D5E' },
            dark_600: { main: '#373751' },
            dark_700: { main: '#1e1e25' },
            dark_800: { main: 'rgba(255,255,255,0.02)' },
            dark_900: { main: '#242538' },
            dark_stroke: { main: '#323f66' },

            teal_600: { main: '#00B894' }
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoAdImg,
          appName,
          calculatedLogoLight,
          calculatedLogoDark,
          calculatedLogo: () => {
            if (mode === 'light') {
              return calculatedLogoLight()
            }
            return calculatedLogoDark()
          }
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appLogoAdImg,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
      calculatedLogoDark,
      calculatedLogoLight
    ]
  )
}
